import React, { useState, useEffect, useCallback } from 'react';

interface TextLegibilityControlsProps {
  onStyleChange: (styles: React.CSSProperties) => void;
  onTextChange: (text: string) => void;
  initialText: string;
}

const TextLegibilityControls: React.FC<TextLegibilityControlsProps> = ({ 
  onStyleChange, 
  onTextChange,
  initialText 
}) => {
  // Text content state
  const [previewText, setPreviewText] = useState(initialText);

  // Basic text styling states
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontSize, setFontSize] = useState(16);
  const [textColor, setTextColor] = useState('#000000');
  const [fontWeight, setFontWeight] = useState('normal');
  const [letterSpacing, setLetterSpacing] = useState(0.5);

  // Text Shadow states
  const [textShadowEnabled, setTextShadowEnabled] = useState(false);
  const [shadowOffsetX, setShadowOffsetX] = useState(0);
  const [shadowOffsetY, setShadowOffsetY] = useState(0);
  const [shadowBlur, setShadowBlur] = useState(0);
  const [shadowColor, setShadowColor] = useState('#000000');
  const [shadowOpacity, setShadowOpacity] = useState(0.5);

  // Stroke states
  const [strokeEnabled, setStrokeEnabled] = useState(false);
  const [strokeWidth, setStrokeWidth] = useState(1);
  const [strokeColor, setStrokeColor] = useState('#ffffff');
  const [strokeOpacity, setStrokeOpacity] = useState(1);

  // Background states
  const [backgroundEnabled, setBackgroundEnabled] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#000000');
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.5);

  // Blend mode state
  const [blendMode, setBlendMode] = useState('normal');

  // Backdrop states
  const [backdropEnabled, setBackdropEnabled] = useState(false);
  const [backdropBlur, setBackdropBlur] = useState(0);
  const [backdropBrightness, setBackdropBrightness] = useState(1);

  // Text alignment state
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right'>('left');
  const [lineHeight, setLineHeight] = useState(1.2);
  const [textSpacing, setTextSpacing] = useState(0);
  const [padding, setPadding] = useState(0);

  // Memoize the style update function
  const updateStyles = useCallback(() => {
    const styles: React.CSSProperties = {
      fontFamily,
      fontSize: `${fontSize}px`,
      color: textColor,
      fontWeight,
      letterSpacing: `${letterSpacing}px`,
      textShadow: textShadowEnabled
        ? `${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px ${shadowColor}${Math.round(shadowOpacity * 255).toString(16).padStart(2, '0')}`
        : 'none',
      WebkitTextStroke: strokeEnabled
        ? `${strokeWidth}px ${strokeColor}${Math.round(strokeOpacity * 255).toString(16).padStart(2, '0')}`
        : 'none',
      mixBlendMode: blendMode as React.CSSProperties['mixBlendMode'],
      textAlign,
      lineHeight,
      padding: `${padding}px`,
      marginBottom: `${textSpacing}px`,
    };

    if (backgroundEnabled) {
      styles.background = `${backgroundColor}${Math.round(backgroundOpacity * 255).toString(16).padStart(2, '0')}`;
      styles.padding = '4px 8px';
      styles.borderRadius = '4px';
    }

    if (backdropEnabled) {
      styles.backdropFilter = `blur(${backdropBlur}px) brightness(${backdropBrightness})`;
    }

    onStyleChange(styles);
  }, [
    fontFamily, fontSize, textColor, fontWeight, letterSpacing,
    textShadowEnabled, shadowOffsetX, shadowOffsetY, shadowBlur, shadowColor, shadowOpacity,
    strokeEnabled, strokeWidth, strokeColor, strokeOpacity,
    backgroundEnabled, backgroundColor, backgroundOpacity,
    blendMode,
    backdropEnabled, backdropBlur, backdropBrightness,
    textAlign, lineHeight, padding, textSpacing,
    onStyleChange
  ]);

  // Update styles when any control changes
  useEffect(() => {
    updateStyles();
  }, [updateStyles]);

  // Update text when preview text changes
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setPreviewText(newText);
    onTextChange(newText);
  }, [onTextChange]);

  // Update preview text when initialText changes
  useEffect(() => {
    setPreviewText(initialText);
  }, [initialText]);

  return (
    <div className="space-y-6 p-4 bg-white rounded-lg shadow">
      <h3 className="text-lg font-medium text-gray-900">Text Styling Controls</h3>

      {/* Preview Text Input */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Preview Text</label>
        <input
          type="text"
          value={previewText}
          onChange={handleTextChange}
          className="w-full p-2 border rounded-md"
        />
      </div>

      {/* Basic Text Styling */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-gray-700">Basic Text Styling</h4>
        <div className="space-y-2 pl-4">
          <div>
            <label className="block text-xs text-gray-600">Font Family</label>
            <select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              className="w-full p-2 border rounded-md"
            >
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Courier New">Courier New</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600">Font Size (px)</label>
            <input
              type="number"
              min="8"
              max="72"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="w-full p-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600">Text Color</label>
            <input
              type="color"
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              className="w-full h-8"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600">Font Weight</label>
            <select
              value={fontWeight}
              onChange={(e) => setFontWeight(e.target.value)}
              className="w-full p-2 border rounded-md"
            >
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
              <option value="lighter">Lighter</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600">Letter Spacing (px)</label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={letterSpacing}
              onChange={(e) => setLetterSpacing(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Text Shadow Controls */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">Text Shadow</label>
          <input
            type="checkbox"
            checked={textShadowEnabled}
            onChange={(e) => setTextShadowEnabled(e.target.checked)}
            className="h-4 w-4 text-blue-600"
          />
        </div>
        {textShadowEnabled && (
          <div className="space-y-2 pl-4">
            <div>
              <label className="block text-xs text-gray-600">Offset X</label>
              <input
                type="range"
                min="-10"
                max="10"
                value={shadowOffsetX}
                onChange={(e) => setShadowOffsetX(Number(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Offset Y</label>
              <input
                type="range"
                min="-10"
                max="10"
                value={shadowOffsetY}
                onChange={(e) => setShadowOffsetY(Number(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Blur</label>
              <input
                type="range"
                min="0"
                max="20"
                value={shadowBlur}
                onChange={(e) => setShadowBlur(Number(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Color</label>
              <input
                type="color"
                value={shadowColor}
                onChange={(e) => setShadowColor(e.target.value)}
                className="w-full h-8"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={shadowOpacity}
                onChange={(e) => setShadowOpacity(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>

      {/* Stroke Controls */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">Text Stroke</label>
          <input
            type="checkbox"
            checked={strokeEnabled}
            onChange={(e) => setStrokeEnabled(e.target.checked)}
            className="h-4 w-4 text-blue-600"
          />
        </div>
        {strokeEnabled && (
          <div className="space-y-2 pl-4">
            <div>
              <label className="block text-xs text-gray-600">Width</label>
              <input
                type="range"
                min="0"
                max="5"
                step="0.5"
                value={strokeWidth}
                onChange={(e) => setStrokeWidth(Number(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Color</label>
              <input
                type="color"
                value={strokeColor}
                onChange={(e) => setStrokeColor(e.target.value)}
                className="w-full h-8"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={strokeOpacity}
                onChange={(e) => setStrokeOpacity(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>

      {/* Background Controls */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">Background Panel</label>
          <input
            type="checkbox"
            checked={backgroundEnabled}
            onChange={(e) => setBackgroundEnabled(e.target.checked)}
            className="h-4 w-4 text-blue-600"
          />
        </div>
        {backgroundEnabled && (
          <div className="space-y-2 pl-4">
            <div>
              <label className="block text-xs text-gray-600">Color</label>
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="w-full h-8"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={backgroundOpacity}
                onChange={(e) => setBackgroundOpacity(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>

      {/* Blend Mode */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Blend Mode</label>
        <select
          value={blendMode}
          onChange={(e) => setBlendMode(e.target.value)}
          className="w-full p-2 border rounded-md"
        >
          <option value="normal">Normal</option>
          <option value="difference">Difference</option>
          <option value="multiply">Multiply</option>
          <option value="screen">Screen</option>
        </select>
      </div>

      {/* Backdrop Controls */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">Backdrop Effects</label>
          <input
            type="checkbox"
            checked={backdropEnabled}
            onChange={(e) => setBackdropEnabled(e.target.checked)}
            className="h-4 w-4 text-blue-600"
          />
        </div>
        {backdropEnabled && (
          <div className="space-y-2 pl-4">
            <div>
              <label className="block text-xs text-gray-600">Blur Amount</label>
              <input
                type="range"
                min="0"
                max="20"
                value={backdropBlur}
                onChange={(e) => setBackdropBlur(Number(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Brightness</label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={backdropBrightness}
                onChange={(e) => setBackdropBrightness(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>

      {/* Text Alignment */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Text Alignment</label>
        <div className="flex space-x-2">
          <button
            onClick={() => setTextAlign('left')}
            className={`px-3 py-1 rounded-md ${
              textAlign === 'left' ? 'bg-blue-500 text-white' : 'bg-gray-200'
            }`}
          >
            Left
          </button>
          <button
            onClick={() => setTextAlign('center')}
            className={`px-3 py-1 rounded-md ${
              textAlign === 'center' ? 'bg-blue-500 text-white' : 'bg-gray-200'
            }`}
          >
            Center
          </button>
          <button
            onClick={() => setTextAlign('right')}
            className={`px-3 py-1 rounded-md ${
              textAlign === 'right' ? 'bg-blue-500 text-white' : 'bg-gray-200'
            }`}
          >
            Right
          </button>
        </div>
      </div>

      {/* Line Height */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Line Height</label>
        <input
          type="range"
          min="1"
          max="2"
          step="0.1"
          value={lineHeight}
          onChange={(e) => setLineHeight(Number(e.target.value))}
          className="w-full"
        />
        <div className="text-sm text-gray-500">{lineHeight.toFixed(1)}</div>
      </div>

      {/* Text Spacing */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Text Spacing (px)</label>
        <input
          type="number"
          min="0"
          max="50"
          value={textSpacing}
          onChange={(e) => setTextSpacing(Number(e.target.value))}
          className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Padding */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Padding (px)</label>
        <input
          type="number"
          min="0"
          max="50"
          value={padding}
          onChange={(e) => setPadding(Number(e.target.value))}
          className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>
  );
};

export default TextLegibilityControls; 